/* ONE BOUT — <ob-hotspot> (S2/H2): "+" hotspot on a photo that opens a small product card.
   Figma: desktop 208:1336 (hotspot 302:6453, card 302:6494), mobile 346:10153 (card 333:4624).
   - The card is a popover="manual" element → rendered in the top layer, so it is never clipped by the
     mobile carousel (overflow-x) nor affected by transforms on ancestor sections.
   - Only one card open at a time; closes on click/tap outside, Esc, focus leaving, or swiping the carousel.
   - Hover-capable pointers: hover previews (closes on leave), click pins it open, second click closes.
     Touch: tap toggles.
   - Positioned above the hotspot, centred on it, clamped to the viewport; flips below when there is no room.
     Prestige's shop-the-look hotspots drive a side product carousel / bottom sheet — a different pattern,
     so only its idea (x/y % hotspot over the image) is reused here. */
const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
let current = null;

class ObHotspot extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this.btn = this.querySelector('.ob-hotspot__btn');
    this.pop = this.querySelector('.ob-hotspot__pop');
    if (!this.btn || !this.pop) return;
    this._ready = true;
    const supported = typeof this.pop.showPopover === 'function';
    this.btn.addEventListener('click', () => {
      if (!supported) { const a = this.pop.querySelector('a[href]'); if (a) window.location.href = a.href; return; }
      if (!this.isOpen) this.open('click');
      else if (this.openedBy === 'hover') this.openedBy = 'click';   // hover preview → click pins it
      else this.close();
    });
    if (!supported) return;
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !HOVER.matches) return;
      clearTimeout(this._t);
      if (!this.isOpen) this.open('hover');
    });
    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse' || this.openedBy !== 'hover') return;
      this._t = setTimeout(() => this.close(), 180);
    });
    this.addEventListener('focusout', (e) => {
      if (this.isOpen && e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
    });
  }

  get isOpen() { return this.pop.matches(':popover-open'); }

  open(by) {
    if (current && current !== this) current.close();
    this.openedBy = by;
    this.pop.showPopover();
    this.place();
    this.classList.add('is-open');
    this.btn.setAttribute('aria-expanded', 'true');
    current = this;
  }

  close() {
    clearTimeout(this._t);
    if (this.isOpen) this.pop.hidePopover();
    this.classList.remove('is-open');
    this.btn.setAttribute('aria-expanded', 'false');
    if (current === this) current = null;
  }

  place() {
    const r = this.btn.getBoundingClientRect();
    const cs = getComputedStyle(this.pop);
    const gap = parseFloat(cs.getPropertyValue('--ob-hs-gap')) || 0;
    const edge = parseFloat(cs.getPropertyValue('--ob-hs-edge')) || 0;
    const vw = document.documentElement.clientWidth;
    const w = this.pop.offsetWidth, h = this.pop.offsetHeight, cx = r.left + r.width / 2;
    const left = Math.max(edge, Math.min(cx - w / 2, vw - w - edge));
    let top = r.top - gap - h;
    const below = top < edge;
    if (below) top = r.bottom + gap;
    this.pop.style.left = `${Math.round(left)}px`;
    this.pop.style.top = `${Math.round(top)}px`;
    this.pop.style.setProperty('--ob-hs-tail-x', `${Math.round(cx - left)}px`);
    this.pop.classList.toggle('is-below', below);
  }
}

if (!customElements.get('ob-hotspot')) {
  customElements.define('ob-hotspot', ObHotspot);
  document.addEventListener('pointerdown', (e) => { if (current && !current.contains(e.target)) current.close(); }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) { const btn = current.btn; current.close(); btn.focus(); }
  });
  // page scroll → follow the hotspot; horizontal swipe of the carousel that holds it → close
  document.addEventListener('scroll', (e) => {
    if (!current) return;
    if (e.target instanceof Element && e.target.contains(current)) current.close();
    else current.place();
  }, { capture: true, passive: true });
  window.addEventListener('resize', () => current && current.place());
}
